import React from "react";
import { VisuallyHidden } from "react-aria";
import type { ComboBoxProps } from "react-aria-components";
import {
    ComboBox,
    FieldError,
    Input,
    Label,
    ListBox,
    ListBoxItem,
    Popover,
    Text,
} from "react-aria-components";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { InputProps } from "./Input";

import styles from "./index.module.scss";

interface Props<T extends object>
    extends InputProps,
        Omit<ComboBoxProps<T>, "children"> {
    // Autocomplete-specific props
    items: Array<T>;
    itemKey: keyof T;
    itemFormatter: (item: T) => string;
    /** Custom item content. Falls back to `itemFormatter`, which is always
     * used as the item's `textValue` for type-ahead and screen readers. */
    itemRender?: (item: T) => React.ReactNode;
    // Optional styling
    popoverClassName?: string;
}

export const AutocompleteField = <T extends object>({
    label,
    labelVisible = true,
    description,
    placeholder,
    items,
    itemKey,
    itemFormatter,
    itemRender,
    popoverClassName,
    ...props
}: Props<T>) => {
    return (
        <ComboBox {...props}>
            <Label
                className={concatClassNames([
                    labelVisible ? styles.label : undefined,
                    props.isRequired ? styles.required : undefined,
                ])}
            >
                {labelVisible ? (
                    label
                ) : (
                    <VisuallyHidden>{label}</VisuallyHidden>
                )}
            </Label>
            <Input className={styles.textInput} placeholder={placeholder} />
            {description && (
                <Text className={styles.description} slot="description">
                    {description}
                </Text>
            )}
            <Popover
                className={concatClassNames([
                    styles.autocompletePopover,
                    popoverClassName,
                ])}
            >
                <ListBox>
                    {items.map((item) => (
                        <ListBoxItem
                            key={String(item[itemKey])}
                            id={String(item[itemKey])}
                            textValue={itemFormatter(item)}
                            className={({ isFocused, isSelected }) =>
                                concatClassNames([
                                    styles.autocompleteListBoxItem,
                                    isFocused
                                        ? styles.autocompleteListBoxItemFocused
                                        : undefined,
                                    isSelected
                                        ? styles.autocompleteListBoxItemSelected
                                        : undefined,
                                ])
                            }
                        >
                            {itemRender
                                ? itemRender(item)
                                : itemFormatter(item)}
                        </ListBoxItem>
                    ))}
                </ListBox>
            </Popover>
            <FieldError className={styles.errorMessage}>
                {({ validationDetails, validationErrors }) =>
                    validationDetails?.valueMissing
                        ? t`This field is required.`
                        : validationErrors
                }
            </FieldError>
        </ComboBox>
    );
};
